import * as React from 'react'

import {
  clearDiagnostics,
  formatDiagnosticsForCopy,
  formatRepeatCount,
} from '@modules/diagnostics/store'
import { useDiagnostics, useIsDiagnosticsEnabled } from '@modules/diagnostics/useDiagnostics'
import { Button } from '@modules/shadcn/ui/Button/Button'

/**
 * 直近の失敗を画面から取り出せるようにする常設パネル。**STGと開発環境だけで出る**。
 *
 * ## なぜトーストではなくパネルか
 *
 * トーストは数秒で消えるので、「読む前に消えた」「そもそも遷移しない失敗（ログイン画面）では
 * 出ていない」という状態になり、非エンジニアが原因を持ち帰れない。消えない置き場所を1つ作り、
 * そこから丸ごとコピーできるようにする。
 *
 * ## 置く位置
 *
 * 画面**左下**に置く。右下は認証後レイアウトの「いえサプリAI」アイコン（`fixed right-0 bottom-0`
 * ・実効80px四方・z-50）が占めており、重ねると互いに押しにくくなる。
 *
 * 有効化は各アプリのprovider（companyは `context/CustomProvider.tsx`）で
 * `setDiagnosticsEnabled` を呼ぶ。無効なら何も描画しない。
 */
export const DiagnosticsPanel: React.FC = () => {
  const entries = useDiagnostics()
  const enabled = useIsDiagnosticsEnabled()
  const [open, setOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(false)

  // 無効な環境（本番）と、まだ失敗が起きていないときは何も出さない
  if (!enabled || entries.length === 0) {
    return null
  }

  const handleCopy = async () => {
    const text = formatDiagnosticsForCopy(entries)
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // クリップボードが使えない環境（権限拒否・非対応）ではコピーを諦める。
      // 一覧は画面に出ているので手で選択できる
      setCopied(false)
    }
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-[calc(100vw-2rem)]">
      {open ? (
        <div className="w-[min(92vw,380px)] rounded-md border border-input bg-card shadow-lg">
          <div className="flex items-center justify-between border-b border-input px-3 py-2">
            <p className="text-sm font-bold">直近の失敗{entries.length}件</p>
            <Button
              className="h-auto px-2 py-1 text-xs"
              onClick={() => setOpen(false)}
              variant="ghost"
            >
              閉じる
            </Button>
          </div>
          <ul className="max-h-[50vh] overflow-y-auto px-3 py-2">
            {entries.map(entry => (
              <li className="border-b border-input py-2 text-xs last:border-b-0" key={entry.id}>
                <p className="text-muted-foreground">
                  {entry.at} / {entry.kind} / {entry.label}
                  {entry.count > 1 && ` / ${formatRepeatCount(entry.count)}`}
                </p>
                <p className="text-destructive break-all">{entry.code}</p>
                {entry.traceId !== undefined && entry.traceId !== '' && (
                  <p className="text-muted-foreground break-all">trace: {entry.traceId}</p>
                )}
                <p className="break-all">{entry.message}</p>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 border-t border-input px-3 py-2">
            {/* コピー文面にはメッセージを含めない（顧客のPIIが入りうるため。store.tsの説明を参照） */}
            <Button className="h-auto px-2 py-1 text-xs" onClick={() => void handleCopy()}>
              {copied ? 'コピーしました' : 'コードとTrace IDをコピー'}
            </Button>
            <Button
              className="h-auto px-2 py-1 text-xs"
              onClick={() => clearDiagnostics()}
              variant="outline"
            >
              消す
            </Button>
          </div>
        </div>
      ) : (
        <Button
          aria-expanded={false}
          className="h-auto px-3 py-2 text-xs"
          onClick={() => setOpen(true)}
          variant="outline"
        >
          診断{entries.length}件
        </Button>
      )}
    </div>
  )
}
